import { isPaidSubscription, SubscriptionType } from '@whispers/sdk';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useCreatePortal, useSendNotification } from '../api.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { PreviewPlaceholder } from '../components/PreviewPlaceholder.js';
import { Spinner } from '../components/Spinner.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useStoryblokPreview } from '../contexts/StoryblokPreviewContext.js';

export const AccountPage = () => {
  const { user, me, logout } = useAuth();
  const { isPreviewMode } = useStoryblokPreview();
  const navigate = useNavigate();
  const createPortal = useCreatePortal();

  const handleLogout = async () => {
    await logout();
    void navigate('/sign-in');
  };

  const handleManageSubscription = () => {
    createPortal.mutate(undefined, {
      onSuccess: ({ url }) => {
        window.location.href = url;
      },
    });
  };

  const sendNotification = useSendNotification();
  const [counter, setCounter] = useState(0);

  const handleSendNotification = () => {
    if (!user) {
      return;
    }
    sendNotification.mutate({
      userId: user.id,
      payload: { title: 'Test Notification', body: 'Hello World!' },
    });
  };

  useEffect(() => {
    if (counter > 0 && counter % 5 === 0) {
      handleSendNotification();
    }
  }, [counter]);

  if (isPreviewMode) {
    return <PreviewPlaceholder />;
  }

  if (!user) {
    return null;
  }

  return (
    <>
      <div>
        <h1
          className="text-xl font-medium text-text"
          onClick={() => setCounter((prev) => prev + 1)}
        >
          Account
        </h1>
      </div>

      {/* Profile info */}
      <Card>
        <div className="grid gap-4">
          <div>
            <p className="text-sm text-text-secondary">Email</p>
            <p className="font-medium text-text">{user.email}</p>
          </div>
          <div>
            <p className="text-sm text-text-secondary">Level</p>
            <p className="font-medium text-text">{me?.level ?? 1}</p>
          </div>
          <div>
            <p className="text-sm text-text-secondary">Subscription</p>
            <p className="font-medium text-text capitalize">
              {user.subscriptionType
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')}
            </p>
          </div>
          {user.role && (
            <div>
              <p className="text-sm text-text-secondary">Role</p>
              <p className="font-medium text-text capitalize">
                {user.role
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ')}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Buy the physical pack */}
      <Card>
        <h2 className="mb-2 text-lg font-medium text-text">Whispers from the Woods</h2>
        <p className="mb-4 text-sm text-text-secondary">
          Get the physical oracle deck from Carys Martin Ceramics.
        </p>
        <Button
          href="https://carysmartinceramics.com/collections/whispers-from-the-woods"
          variant="secondary"
          target="_blank"
          rel="noopener noreferrer"
        >
          Buy the physical pack
        </Button>
      </Card>

      {/* Subscription management */}
      {user.subscriptionType === SubscriptionType.enum.FREE && (
        <Card>
          <h2 className="mb-3 text-lg font-medium text-text">Upgrade</h2>
          <p className="mb-4 text-sm text-text-secondary">
            Get unlimited readings and see your card journey unfold in your feed.
          </p>
          <Button href="/feeds">View your feed</Button>
        </Card>
      )}

      {isPaidSubscription(user) && (
        <Card>
          <h2 className="mb-3 text-lg font-medium text-text">Subscription</h2>
          <p className="mb-4 text-sm text-text-secondary">You have an active subscription.</p>
          <Button
            variant="ghost"
            onClick={handleManageSubscription}
            disabled={createPortal.isPending}
          >
            {createPortal.isPending ? <Spinner size="sm" /> : 'Manage subscription'}
          </Button>
        </Card>
      )}

      {/* Sign out */}
      <Card>
        <Button variant="danger" onClick={handleLogout} className="w-full">
          Sign out
        </Button>
      </Card>

      {/* Footer links */}
      <div className="grid gap-2 pb-4 text-center text-sm text-text-secondary">
        <Link to="/terms-and-conditions" className="hover:text-text">
          Terms and Conditions
        </Link>
        <Link to="/privacy-policy" className="hover:text-text">
          Privacy Policy
        </Link>
        <a
          href="https://carysmartinceramics.com/pages/contact-us"
          className="hover:text-text"
          target="_blank"
          rel="noopener noreferrer"
        >
          Contact Us
        </a>
        <a
          href="https://carysmartinceramics.com"
          className="hover:text-text"
          target="_blank"
          rel="noopener noreferrer"
        >
          carysmartinceramics.com
        </a>
      </div>
    </>
  );
};
